/**
 * Shared low-level builders for the district exterior — the primitives every
 * other district module composes: a matte material, a collidable box, and a
 * canvas-drawn sign texture. Kept in one place so the concern-specific modules
 * (buildings, entrances, furniture) don't each re-implement them.
 */
import * as pc from 'playcanvas'

// ── Palette ───────────────────────────────────────────────────────────────
export const KERB = new pc.Color(0.66, 0.65, 0.63)
export const MASSING = new pc.Color(0.62, 0.60, 0.58)
export const GROUND_FLOOR = new pc.Color(0.42, 0.41, 0.40)
export const PIER = new pc.Color(0.50, 0.49, 0.47)

export function matte(color: pc.Color, gloss: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial()
  m.diffuse = color
  m.useMetalness = true
  m.metalness = 0
  m.gloss = gloss
  m.update()
  return m
}

/**
 * A box mesh under `parent`. castShadows defaults OFF — most district boxes are
 * backdrop that receives but does not cast; only the pieces whose shadow a
 * player actually reads (the gate, the door canopies) opt in.
 */
export function box(
  parent: pc.Entity,
  name: string,
  pos: [number, number, number],
  scale: [number, number, number],
  mat: pc.Material,
  castShadows = false,
): pc.Entity {
  const e = new pc.Entity(name)
  e.addComponent('render', { type: 'box' })
  if (e.render) {
    for (const mi of e.render.meshInstances) mi.material = mat
    e.render.castShadows = castShadows
  }
  e.setLocalScale(scale[0], scale[1], scale[2])
  e.setPosition(pos[0], pos[1], pos[2])
  parent.addChild(e)
  return e
}

/** A dark fascia sign carrying `text`, drawn to a canvas. Zero download. */
export function signTexture(app: pc.Application, text: string): pc.Texture {
  const W = 512, H = 96
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  const tex = new pc.Texture(app.graphicsDevice, {
    width: W, height: H,
    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
  })
  if (ctx) {
    ctx.fillStyle = '#15181d'
    ctx.fillRect(0, 0, W, H)
    ctx.fillStyle = '#f2ede4'
    ctx.font = '600 52px system-ui, -apple-system, Segoe UI, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.letterSpacing = '6px'
    ctx.fillText(text, W / 2, H / 2 + 2)
    tex.setSource(canvas)
  }
  return tex
}

// ── Time of day ─────────────────────────────────────────────────────────────
// The district ships in DAY: window emissive is off and lamp posts are dark, so
// nothing about the lit scene changes. Flip DISTRICT_TIME to 'dusk' and one
// shared emissive mask lights every window and the lamps switch on. A module
// const on purpose — the capability exists without a store field or UI.
export type TimeOfDay = 'day' | 'dusk'
export const DISTRICT_TIME: TimeOfDay = 'day'
export const IS_DUSK = (DISTRICT_TIME as TimeOfDay) === 'dusk'
/** Emissive multiplier for lit windows — 0 by day, a warm glow at dusk. */
export const WINDOW_EMISSIVE_INTENSITY = IS_DUSK ? 6 : 0
/** The warm interior light that spills from the glass at dusk. */
export const WINDOW_EMISSIVE = new pc.Color(1.0, 0.86, 0.60)
